/**
 * Enum of Drupal node displays.
 */

export enum DRUPAL_NODE_DISPLAY {
  Default = 'default',
  Tile = 'tile',
  Card = 'card',
  Label = 'label',
  Teaser = 'teaser',
  Featured = 'featured',
  SmallCard = 'small_card',
  Collapsible = 'collapsible',
  FeaturedTile = 'featured_tile',
}

/**
 * Type definition of a Drupal node.
 */

export type DrupalNode = {
  id: number;
  type: 'node';
  bundle: string;
  display: DRUPAL_NODE_DISPLAY;
  title: string;
};

/**
 * Type definition of Drupal node page.
 */

export type DrupalNodePage = DrupalNode & {
  alias: string;
};

/**
 * Enum of Drupal term displays.
 */

export enum DRUPAL_TERM_DISPLAY {
  Default = 'default',
  Label = 'label',
  Button = 'button',
}

/**
 * Type definition of a Drupal taxonomy term.
 */

export type DrupalTerm = {
  id: number;
  name: string;
  type: 'taxonomy_term';
  bundle: string;
  display: DRUPAL_TERM_DISPLAY;
};

/**
 * Enum of Drupal media types.
 */

export enum DRUPAL_MEDIA_BUNDLE {
  Image = 'image',
  Document = 'document',
  Audio = 'audio',
  Video = 'video',
  RemoteVideo = 'remote_video',
}

/**
 * Type definition of a Drupal media.
 */

export type DrupalMedia = {
  id: number;
  url: string;
  type: 'media';
  bundle: DRUPAL_MEDIA_BUNDLE;
  name: string;
};

/**
 * Type definition of a Drupal media image.
 */

export type DrupalImage = DrupalMedia & {
  bundle: DRUPAL_MEDIA_BUNDLE.Image;
  alt: string;
  width: number;
  height: number;
  optimize?: boolean;
  focalPoint?: {
    absolute: { x: number; y: number };
    relative: { x: string; y: string };
  };
};

/**
 * Type definition of a Drupal media document.
 */

export type DrupalDocument = DrupalMedia & {
  bundle: DRUPAL_MEDIA_BUNDLE.Document;
};

/**
 * Type definition of a Drupal media audio.
 */

export type DrupalAudio = DrupalMedia & {
  bundle: DRUPAL_MEDIA_BUNDLE.Audio;
};

/**
 * Type definition of a Drupal media video.
 */

export type DrupalVideo = DrupalMedia & {
  bundle: DRUPAL_MEDIA_BUNDLE.Video;
};

/**
 * Type definition of a Drupal media remote video.
 */

export type DrupalRemoteVideo = DrupalMedia & {
  bundle: DRUPAL_MEDIA_BUNDLE.RemoteVideo;
};

/**
 * Type definition of a Drupal link.
 */

export type DrupalLink = {
  url: string;
  text: string;
};

/**
 * Type definition of the attributes of a Drupal layout builder block.
 */

export type DrupalBlockAttributes = {
  id?: string;
  style?: string;
  classes?: string;
  attributes?: Record<string, string>;
};

/**
 * Type definition of a Drupal block.
 *
 * The content is different for each block type, so we try to infer the type with a generic.
 */

export type DrupalBlock<C = object> = {
  /**
   * The UUID of the Drupal block entity.
   */
  uuid: string;

  /**
   * The Drupal entity type.
   */
  type: 'block';

  /**
   * The bundle of the block.
   */
  bundle: string;

  /**
   * The administrative label of the block.
   */
  label: string;

  /**
   * The section region where the block is placed.
   */
  region: string;

  /**
   * The weight of the block in the section region.
   */
  weight: number;

  /**
   * The content of the block.
   *
   * A generic is used to infer the type of the block content.
   */
  content: C;

  /**
   * Whether the block should animate when entering the viewport or not.
   */
  animate: boolean;

  /**
   * The attributes of the block (id, style, classes, attributes).
   */
  attributes: DrupalBlockAttributes;

  /**
   * A boolean to indicate if the editor should be disabled for the block.
   *
   * Mostly used for nested blocks, since its not supported at the moment.
   */
  disableEditor?: boolean;
};

/**
 * Type definition of the attributes of a Drupal layout builder section.
 */

export type DrupalSectionAttributes = {
  id?: string;
  classes?: string;
  attributes?: string[];
};

/**
 * Type definition of a Drupal layout builder section.
 *
 * The settings is different for each section type, so we try to infer the type with a generic.
 */

export type DrupalSection<S = object> = {
  type: string;
  label: string;
  weight: number;
  blocks: Array<DrupalBlock | null>;
  settings: S;
  attributes: DrupalSectionAttributes;
};

/**
 * Type definition of a Drupal view.
 */

export type DrupalView<
  Content = DrupalNode,
  Filters = Record<string, unknown>,
  SettingsFilters = Record<keyof Filters, unknown>,
> = {
  /**
   * The Drupal machine name of the view.
   */
  id: string;

  /**
   * The display identifier of the view.
   */
  display: string;

  /**
   * The first page of view results.
   */
  content: Content[];

  /**
   * The view exposed filters and their values.
   */
  filters: Filters;

  /**
   * The current view settings.
   */
  settings: {
    /**
     * The type of Drupal view pager.
     *
     * - `full`: A load more button should be displayed.
     * - `mini`: Not used at the moment.
     * - `some`: No pager is shown, but the amount of results is limited.
     * - `none`: No pager is shown and all results are displayed.
     */
    pager?: 'full' | 'mini' | 'some' | 'none';

    /**
     * The number of items fetched.
     */
    limit?: string;

    /**
     * The number of items skipped.
     */
    offset?: string;

    /**
     * The current view filter values.
     */
    filters?: SettingsFilters;

    /**
     * The view contextual filters.
     */
    argument?: string[];
  };
};

/**
 * Type definition of a Drupal paragraph.
 */

export type DrupalParagraph = {
  id: string;
  type: 'paragraph';
  bundle: string;
};

/**
 * Type definition for the value of a Drupal text field.
 *
 * Also used for list of texts fields.
 */

export type DrupalText = {
  value?: string;
};

/**
 * Type definition for the value of a Drupal color widget.
 */

export type DrupalColor = {
  color: string;
  opacity: number;
};

/**
 * Type definition for the read time of a Drupal page.
 */

export type DrupalReadTime = {
  auto: boolean;
  minutes: number;
  seconds: number;
};
